import React, { useState, useEffect } from 'react';
import {
    LifeBuoy, Shield, AlertCircle, Clock, CheckCircle, XCircle,
    Search, Filter, Download, RefreshCw, Eye, MessageCircle,
    User, Calendar, Paperclip, Hash, ArrowUpRight, ArrowDownRight,
    TrendingUp, Users, FileText, Settings, Bell, LogOut,
    ChevronRight, Edit, Trash2, UserPlus, Mail, Phone , MonitorCog , SquareActivity , Tickets
} from 'lucide-react';
import ApiService from '../services/api';
import { useNavigate } from 'react-router-dom';


const AdminSupportDashboard = () => {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [authError, setAuthError] = useState('');
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({});
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [showTicketDetail, setShowTicketDetail] = useState(false);
    const [resolutionMessage, setResolutionMessage] = useState('');
    const [showResolutionModal, setShowResolutionModal] = useState(false);
    const [ticketToResolve, setTicketToResolve] = useState(null);

// Default to showing only active tickets
    const [filters, setFilters] = useState({
        status: 'active', // Shows only open + in-progress
        priority: 'all',
        category: 'all',
        search: ''
    });

    const [pagination, setPagination] = useState({
        page: 1,
        pages: 1,
        total: 0,
        limit: 15
    });

    // Categories from your support system
    const categories = [
        { id: 'all', name: 'All Categories', icon: '📋' },
        { id: 'technical-issue', name: 'Technical Issue', icon: '🔧' },
        { id: 'account-access', name: 'Account & Access', icon: '🔐' },
        { id: 'feature-request', name: 'Feature Request', icon: '⚡' },
        { id: 'data-management', name: 'Data Management', icon: '📊' },
        { id: 'training-support', name: 'Training & Documentation', icon: '📚' },
        { id: 'system-performance', name: 'Performance Issues', icon: '⚙️' },
        { id: 'integration-api', name: 'API & Integrations', icon: '🔗' },
        { id: 'other', name: 'Other', icon: '❓' }
    ];

    // Authentication check
    const handleAuth = (e) => {
        e.preventDefault();
        if (password === 'IT20245') {
            setIsAuthenticated(true);
            setAuthError('');
            fetchTickets();
            fetchStats();
        } else {
            setAuthError('Invalid access code. Please contact IT administrator.');
        }
    };

    // Fetch all tickets
    const fetchTickets = async () => {
        try {
            setLoading(true);
            const params = {
                ...filters,
                page: pagination.page,
                limit: pagination.limit
            };

            // Remove 'all' values
            Object.keys(params).forEach(key => {
                if (params[key] === 'all') {
                    delete params[key];
                }
            });

            const response = await ApiService.getSupportTickets(params);

            if (response.success) {
                setTickets(response.tickets || []);
                setPagination(prev => ({
                    ...prev,
                    pages: response.pagination?.pages || 1,
                    total: response.pagination?.total || 0
                }));
            }
        } catch (error) {
            console.error('Error fetching tickets:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch statistics
    const fetchStats = async () => {
        try {
            const response = await ApiService.getSupportStats();
            if (response.success) {
                setStats(response.stats || {});
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchTickets();
        }
    }, [filters, pagination.page, isAuthenticated]);

    const getStatusColor = (status) => {
        const colors = {
            'open': 'status-badge status-out-of-stock',
            'in-progress': 'status-badge status-low-stock',
            'resolved': 'status-badge status-available',
            'closed': 'status-badge'
        };
        return colors[status] || colors.open;
    };

    const getPriorityColor = (priority) => {
        const colors = {
            'low': '#059669',
            'medium': '#f59e0b',
            'high': '#ef4444',
            'critical': '#dc2626'
        };
        return colors[priority] || colors.medium;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const calculateAge = (createdAt) => {
        const now = new Date();
        const created = new Date(createdAt);
        const diffMs = now - created;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        if (diffHours < 24) {
            return `${diffHours}h`;
        } else {
            return `${diffDays}d`;
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleViewTicket = async (ticket) => {
        setSelectedTicket(ticket);
        setShowTicketDetail(true);
    };

    const handleStatusUpdate = async (ticketId, newStatus, resolutionMsg = '') => {
        try {
            const payload = { status: newStatus };
            if (resolutionMsg) {
                payload.resolutionMessage = resolutionMsg;
            }

            const response = await ApiService.updateTicketStatus(ticketId, payload);
            if (response.success) {
                fetchTickets();
                fetchStats();
                setShowTicketDetail(false);
                if (newStatus === 'resolved') {
                    alert('✅ Ticket marked as resolved! Customer has been notified via email.');
                }
            }
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Error updating ticket status: ' + error.message);
        }
    };

    const handleResolveWithMessage = (ticket) => {
        setTicketToResolve(ticket);
        setShowResolutionModal(true);
        setResolutionMessage('');
    };

    const submitResolution = () => {
        if (ticketToResolve) {
            handleStatusUpdate(ticketToResolve.ticketId, 'resolved', resolutionMessage);
            setShowResolutionModal(false);
            setTicketToResolve(null);
            setResolutionMessage('');
        }
    };


    // Login Screen
    if (!isAuthenticated) {
        return (
            <div className="dashboard-container" style={{ justifyContent: 'center', alignItems: 'center' }}>
                <div style={{
                    background: 'white',
                    padding: '48px',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(30, 64, 175, 0.1)',
                    width: '100%',
                    maxWidth: '400px'
                }}>
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            background: 'linear-gradient(135deg, #1e40af, #0ea5e9)',
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px'
                        }}>
                            <Shield style={{ width: '32px', height: '32px', color: 'white' }} />
                        </div>
                        <h1 className="page-title" style={{ marginBottom: '8px' }}>IT Support Admin</h1>
                        <p className="page-subtitle" style={{ marginBottom: '0' }}>Enter access code to continue</p>
                    </div>

                    <form onSubmit={handleAuth}>
                        <div style={{ marginBottom: '24px' }}>
                            <label className="filter-label" style={{ marginBottom: '8px', display: 'block' }}>
                                Access Code
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="filter-input"
                                style={{ width: '100%' }}
                                placeholder="Enter IT access code"
                                required
                            />
                        </div>

                        {authError && (
                            <div style={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                color: '#ef4444',
                                padding: '12px',
                                borderRadius: '8px',
                                fontSize: '14px',
                                marginBottom: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <AlertCircle style={{ width: '16px', height: '16px' }} />
                                {authError}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="action-button"
                            style={{ width: '100%', justifyContent: 'center' }}
                        >
                            <Shield style={{ width: '16px', height: '16px' }} />
                            Access Dashboard
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // Main Dashboard
    return (
        <div className="dashboard-container">
            {/* Sidebar */}
            <div className="sidebar">
                <div className="sidebar-header">
                    <div className="sidebar-logo-container">
                        <div className="sidebar-logo">
                            <LifeBuoy style={{ width: '24px', height: '24px', color: 'white' }} />
                        </div>
                        <div className="sidebar-brand">
                            <h1 className="sidebar-title">IT Support</h1>
                            <p className="sidebar-subtitle">Admin Dashboard</p>
                        </div>
                    </div>
                </div>

                <nav className="sidebar-nav">
                    <div className="nav-item active">
                        <FileText style={{ width: '20px', height: '20px' }} />
                        <span className="nav-text">All Tickets</span>
                    </div>
                    <div
                        className="nav-item"
                        onClick={() => navigate('/dashboard')}
                        style={{ cursor: 'pointer' }}
                    >
                        <FileText style={{ width: '20px', height: '20px' }} />
                        <span className="nav-text">Dashboard</span>
                    </div>
                    <div className="nav-item">
                        <TrendingUp style={{ width: '20px', height: '20px' }} />
                        <span className="nav-text">Analytics</span>
                    </div>
                    <div className="nav-item">
                        <Users style={{ width: '20px', height: '20px' }} />
                        <span className="nav-text">Team</span>
                    </div>
                    <div className="nav-item">
                        <Settings style={{ width: '20px', height: '20px' }} />
                        <span className="nav-text">Settings</span>
                    </div>
                </nav>

                <div className="sidebar-footer">
                    <div className="user-profile">
                        <div className="user-avatar">IT</div>
                        <div className="user-info">
                            <p className="user-name">IT Administrator</p>
                            <p className="user-role">Support Admin</p>
                        </div>
                        <button
                            className="logout-button"
                            onClick={() => setIsAuthenticated(false)}
                            title="Logout"
                        >
                            <LogOut style={{ width: '16px', height: '16px' }} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="main-content">
                {/* Header */}
                <div className="header">
                    <div className="header-content">
                        <div className="header-left">
                            <div className="breadcrumb">
                                <div className="breadcrumb-item">
                                    <LifeBuoy style={{ width: '16px', height: '16px' }} />
                                    <span>Support</span>
                                </div>
                                <ChevronRight style={{ width: '14px', height: '14px' }} />
                                <div className="breadcrumb-item">
                                    <span style={{ color: '#1e40af', fontWeight: '600' }}>Admin Dashboard</span>
                                </div>
                            </div>
                        </div>

                        <div className="header-right">
                            <div className="search-container">
                                <Search className="search-icon" style={{ width: '18px', height: '18px' }} />
                                <input
                                    type="text"
                                    placeholder="Search tickets..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className="search-input"
                                />
                            </div>

                            <div className="header-actions">
                                <button
                                    className="header-button"
                                    onClick={() => fetchTickets()}
                                    title="Refresh"
                                >
                                    <RefreshCw style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button className="header-button" title="Notifications">
                                    <Bell style={{ width: '18px', height: '18px' }} />
                                    {stats.openTickets > 0 && (
                                        <span className="notification-badge">{stats.openTickets}</span>
                                    )}
                                </button>

                                <button className="header-button" title="Export">
                                    <Download style={{ width: '18px', height: '18px' }} />
                                </button>

                                <div className="time-display">
                                    {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="products-content">
                    {/* Page Header */}
                    <div className="page-header">
                        <h1 className="page-title">Support Tickets Management</h1>
                        <p className="page-subtitle">
                            Monitor and manage all customer support requests in real-time
                        </p>
                    </div>

                    {/* Stats Cards */}
                    <div className="categories-grid" style={{ marginBottom: '32px' }}>
                        <div className="category-card">
                            <div className="category-header">
                                <div className="category-icon">
                                    <FileText style={{ width: '20px', height: '20px', color: 'white' }} />
                                </div>
                                <div>
                                    <h3 className="category-name">Total Tickets</h3>
                                    <p className="category-count">{stats.totalTickets || 0}</p>
                                </div>
                            </div>
                        </div>

                        <div className="category-card">
                            <div className="category-header">
                                <div className="category-icon" style={{ background: '#ef4444' }}>
                                    <AlertCircle style={{ width: '20px', height: '20px', color: 'white' }} />
                                </div>
                                <div>
                                    <h3 className="category-name">Open</h3>
                                    <p className="category-count" style={{ color: '#ef4444' }}>{stats.openTickets || 0}</p>
                                </div>
                            </div>
                        </div>

                        <div className="category-card">
                            <div className="category-header">
                                <div className="category-icon" style={{ background: '#f59e0b' }}>
                                    <Clock style={{ width: '20px', height: '20px', color: 'white' }} />
                                </div>
                                <div>
                                    <h3 className="category-name">In Progress</h3>
                                    <p className="category-count" style={{ color: '#f59e0b' }}>{stats.inProgressTickets || 0}</p>
                                </div>
                            </div>
                        </div>

                        <div className="category-card">
                            <div className="category-header">
                                <div className="category-icon" style={{ background: '#059669' }}>
                                    <CheckCircle style={{ width: '20px', height: '20px', color: 'white' }} />
                                </div>
                                <div>
                                    <h3 className="category-name">Resolved</h3>
                                    <p className="category-count" style={{ color: '#059669' }}>{stats.resolvedTickets || 0}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="filters-section">
                        <div className="filters-row">
                            <div className="filter-group">
                                <label className="filter-label">Status</label>
                                <select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="all">All Status</option>
                                    <option value="open">Open</option>
                                    <option value="in-progress">In Progress</option>
                                    <option value="resolved">Resolved</option>
                                    <option value="closed">Closed</option>
                                </select>
                            </div>

                            <div className="filter-group">
                                <label className="filter-label">Priority</label>
                                <select
                                    value={filters.priority}
                                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="all">All Priority</option>
                                    <option value="critical">Critical</option>
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                </select>
                            </div>

                            <div className="filter-group">
                                <label className="filter-label">Category</label>
                                <select
                                    value={filters.category}
                                    onChange={(e) => handleFilterChange('category', e.target.value)}
                                    className="filter-select"
                                >
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label className="filter-label">Sort By</label>
                                <select
                                    value={filters.sortBy}
                                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="createdAt">Date Created</option>
                                    <option value="priority">Priority</option>
                                    <option value="status">Status</option>
                                    <option value="updatedAt">Last Updated</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Tickets Table */}
                    <div className="products-table-section">
                        <div className="table-header">
                            <h2 className="table-title">Support Tickets</h2>
                            <div className="table-stats">
                                {loading ? 'Loading...' : `${pagination.total} tickets found`}
                            </div>
                        </div>

                        {loading ? (
                            <div style={{ padding: '48px', textAlign: 'center' }}>
                                <RefreshCw style={{ width: '32px', height: '32px', animation: 'spin 1s linear infinite', color: '#1e40af' }} />
                                <p style={{ marginTop: '16px', color: '#64748b' }}>Loading tickets...</p>
                            </div>
                        ) : (
                            <table className="products-table">
                                <thead className="table-head">
                                <tr>
                                    <th>Ticket ID</th>
                                    <th>Subject</th>
                                    <th>Customer</th>
                                    <th>Status</th>
                                    <th>Priority</th>
                                    <th>Category</th>
                                    <th>Created</th>
                                    <th>Age</th>
                                    <th>Actions</th>
                                </tr>
                                </thead>
                                <tbody className="table-body">
                                {tickets.map((ticket) => (
                                    <tr key={ticket._id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Hash style={{ width: '12px', height: '12px', color: '#64748b' }} />
                                                <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                                                        {ticket.ticketId}
                                                    </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div>
                                                <div className="product-name">{ticket.subject}</div>
                                                <div className="product-sku">
                                                    {ticket.description.substring(0, 60)}...
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                    <span style={{ fontWeight: '600' }}>
                                                        {ticket.userInfo.fullName || `${ticket.userInfo.firstName} ${ticket.userInfo.lastName}`.trim() || 'Unknown'}
                                                    </span>
                                                <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                        {ticket.userInfo.email}
                                                    </span>
                                            </div>
                                        </td>
                                        <td>
                                                <span className={getStatusColor(ticket.status)}>
                                                    {ticket.status.replace('-', ' ').toUpperCase()}
                                                </span>
                                        </td>
                                        <td>
                                                <span style={{
                                                    color: getPriorityColor(ticket.priority),
                                                    fontWeight: '600',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {ticket.priority}
                                                </span>
                                        </td>
                                        <td style={{ fontSize: '13px' }}>
                                            {ticket.category.replace('-', ' ')}
                                        </td>
                                        <td style={{ fontSize: '13px' }}>
                                            {formatDate(ticket.createdAt)}
                                        </td>
                                        <td>
                                                <span style={{
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    color: '#64748b'
                                                }}>
                                                    {calculateAge(ticket.createdAt)}
                                                </span>
                                        </td>
                                        <td>
                                            <div className="actions-cell">
                                                <button
                                                    className="action-icon"
                                                    onClick={() => handleViewTicket(ticket)}
                                                    title="View Details"
                                                >
                                                    <Eye style={{ width: '16px', height: '16px' }} />
                                                </button>

                                                {ticket.responses && ticket.responses.length > 0 && (
                                                    <span style={{
                                                        fontSize: '12px',
                                                        color: '#1e40af',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                            <MessageCircle style={{ width: '12px', height: '12px' }} />
                                                        {ticket.responses.length}
                                                        </span>
                                                )}

                                                {ticket.attachments && ticket.attachments.length > 0 && (
                                                    <span style={{
                                                        fontSize: '12px',
                                                        color: '#64748b',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                            <Paperclip style={{ width: '12px', height: '12px' }} />
                                                        {ticket.attachments.length}
                                                        </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <div style={{
                                padding: '20px 24px',
                                borderTop: '1px solid rgba(30, 64, 175, 0.1)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <div style={{ fontSize: '14px', color: '#64748b' }}>
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} tickets
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        className="action-button secondary"
                                        style={{ fontSize: '14px', padding: '8px 16px' }}
                                    >
                                        Previous
                                    </button>
                                    <span style={{
                                        padding: '8px 16px',
                                        background: '#1e40af',
                                        color: 'white',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontWeight: '600'
                                    }}>
                                        {pagination.page}
                                    </span>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page === pagination.pages}
                                        className="action-button secondary"
                                        style={{ fontSize: '14px', padding: '8px 16px' }}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Ticket Detail Modal */}
            {showTicketDetail && selectedTicket && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        width: '100%',
                        maxWidth: '800px',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{
                            padding: '24px',
                            borderBottom: '1px solid rgba(30, 64, 175, 0.1)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div>
                                <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700' }}>
                                    Ticket Details
                                </h2>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Hash style={{ width: '14px', height: '14px', color: '#64748b' }} />
                                    <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                                        {selectedTicket.ticketId}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowTicketDetail(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: '8px',
                                    cursor: 'pointer',
                                    borderRadius: '8px',
                                    color: '#64748b'
                                }}
                            >
                                <XCircle style={{ width: '20px', height: '20px' }} />
                            </button>
                        </div>

                        <div style={{ padding: '24px' }}>
                            <div style={{ marginBottom: '24px' }}>
                                <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
                                    {selectedTicket.subject}
                                </h3>
                                <p style={{ margin: '0', color: '#64748b', lineHeight: '1.6' }}>
                                    {selectedTicket.description}
                                </p>
                            </div>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '16px',
                                marginBottom: '24px'
                            }}>
                                <div>
                                    <strong style={{ color: '#374151' }}>Customer:</strong>
                                    <div style={{ marginTop: '4px' }}>
                                        <div>{selectedTicket.userInfo.fullName || 'Unknown'}</div>
                                        <div style={{ fontSize: '14px', color: '#64748b' }}>
                                            {selectedTicket.userInfo.email}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <strong style={{ color: '#374151' }}>Status:</strong>
                                    <div style={{ marginTop: '4px' }}>
                                        <span className={getStatusColor(selectedTicket.status)}>
                                            {selectedTicket.status.replace('-', ' ').toUpperCase()}
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <strong style={{ color: '#374151' }}>Priority:</strong>
                                    <div style={{
                                        marginTop: '4px',
                                        color: getPriorityColor(selectedTicket.priority),
                                        fontWeight: '600',
                                        textTransform: 'uppercase'
                                    }}>
                                        {selectedTicket.priority}
                                    </div>
                                </div>

                                <div>
                                    <strong style={{ color: '#374151' }}>Category:</strong>
                                    <div style={{ marginTop: '4px' }}>
                                        {selectedTicket.category.replace('-', ' ')}
                                    </div>
                                </div>

                                <div>
                                    <strong style={{ color: '#374151' }}>Created:</strong>
                                    <div style={{ marginTop: '4px', fontSize: '14px' }}>
                                        {formatDate(selectedTicket.createdAt)}
                                    </div>
                                </div>

                                <div>
                                    <strong style={{ color: '#374151' }}>Age:</strong>
                                    <div style={{ marginTop: '4px', fontWeight: '600' }}>
                                        {calculateAge(selectedTicket.createdAt)} ago
                                    </div>
                                </div>
                            </div>

                            {/* Contact Information */}
                            {selectedTicket.contactInfo && (
                                <div style={{ marginBottom: '24px' }}>
                                    <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                                        Contact Information
                                    </h4>
                                    <div style={{
                                        background: '#f8fafc',
                                        padding: '16px',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(30, 64, 175, 0.1)'
                                    }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                                            <div>
                                                <strong style={{ fontSize: '14px', color: '#374151' }}>Preferred Contact:</strong>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                                                    {selectedTicket.contactMethod === 'phone' ? (
                                                        <Phone style={{ width: '14px', height: '14px', color: '#1e40af' }} />
                                                    ) : (
                                                        <Mail style={{ width: '14px', height: '14px', color: '#1e40af' }} />
                                                    )}
                                                    <span style={{ fontSize: '14px' }}>
                                                        {selectedTicket.contactMethod.charAt(0).toUpperCase() + selectedTicket.contactMethod.slice(1)}
                                                    </span>
                                                </div>
                                            </div>

                                            {selectedTicket.contactInfo.phone && (
                                                <div>
                                                    <strong style={{ fontSize: '14px', color: '#374151' }}>Phone:</strong>
                                                    <div style={{ fontSize: '14px', marginTop: '4px' }}>
                                                        {selectedTicket.contactInfo.phone}
                                                    </div>
                                                </div>
                                            )}

                                            {selectedTicket.contactInfo.preferredTime && (
                                                <div>
                                                    <strong style={{ fontSize: '14px', color: '#374151' }}>Preferred Time:</strong>
                                                    <div style={{ fontSize: '14px', marginTop: '4px' }}>
                                                        {selectedTicket.contactInfo.preferredTime}
                                                    </div>
                                                </div>
                                            )}

                                            <div>
                                                <strong style={{ fontSize: '14px', color: '#374151' }}>Timezone:</strong>
                                                <div style={{ fontSize: '14px', marginTop: '4px' }}>
                                                    {selectedTicket.contactInfo.timezone || 'UTC+3'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* System Information */}
                            {selectedTicket.systemInfo && (
                                <div style={{ marginBottom: '24px' }}>
                                    <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                                        System Information
                                    </h4>
                                    <div style={{
                                        background: '#f8fafc',
                                        padding: '16px',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(30, 64, 175, 0.1)'
                                    }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                                            {selectedTicket.systemInfo.browser && (
                                                <div>
                                                    <strong style={{ fontSize: '14px', color: '#374151' }}>Browser:</strong>
                                                    <div style={{ fontSize: '13px', fontFamily: 'monospace', marginTop: '4px' }}>
                                                        {selectedTicket.systemInfo.browser}
                                                    </div>
                                                </div>
                                            )}

                                            {selectedTicket.systemInfo.operatingSystem && (
                                                <div>
                                                    <strong style={{ fontSize: '14px', color: '#374151' }}>OS:</strong>
                                                    <div style={{ fontSize: '13px', fontFamily: 'monospace', marginTop: '4px' }}>
                                                        {selectedTicket.systemInfo.operatingSystem}
                                                    </div>
                                                </div>
                                            )}

                                            {selectedTicket.systemInfo.screenResolution && (
                                                <div>
                                                    <strong style={{ fontSize: '14px', color: '#374151' }}>Resolution:</strong>
                                                    <div style={{ fontSize: '13px', fontFamily: 'monospace', marginTop: '4px' }}>
                                                        {selectedTicket.systemInfo.screenResolution}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Attachments */}
                            {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                                <div style={{ marginBottom: '24px' }}>
                                    <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                                        Attachments ({selectedTicket.attachments.length})
                                    </h4>
                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        {selectedTicket.attachments.map((attachment, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '12px 16px',
                                                    background: 'white',
                                                    border: '1px solid rgba(30, 64, 175, 0.1)',
                                                    borderRadius: '8px'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <Paperclip style={{ width: '16px', height: '16px', color: '#64748b' }} />
                                                    <div>
                                                        <div style={{ fontSize: '14px', fontWeight: '500' }}>
                                                            {attachment.originalName || attachment.filename}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                            {attachment.size ? (attachment.size / 1024 / 1024).toFixed(2) + ' MB' : 'Unknown size'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: '#1e40af',
                                                        cursor: 'pointer',
                                                        padding: '4px',
                                                        borderRadius: '4px'
                                                    }}
                                                    title="Download"
                                                >
                                                    <Download style={{ width: '16px', height: '16px' }} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Responses */}
                            {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                                <div style={{ marginBottom: '24px' }}>
                                    <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                                        Responses ({selectedTicket.responses.length})
                                    </h4>
                                    <div style={{ display: 'grid', gap: '12px' }}>
                                        {selectedTicket.responses.map((response, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    padding: '16px',
                                                    background: response.isInternal ? '#fff7ed' : '#f0f9ff',
                                                    border: `1px solid ${response.isInternal ? '#fed7aa' : '#bfdbfe'}`,
                                                    borderRadius: '8px',
                                                    borderLeft: `4px solid ${response.isInternal ? '#f59e0b' : '#0ea5e9'}`
                                                }}
                                            >
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: '8px'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <User style={{ width: '14px', height: '14px', color: '#64748b' }} />
                                                        <span style={{ fontSize: '14px', fontWeight: '600' }}>
                                                            {response.responderInfo?.name || 'Support Team'}
                                                        </span>
                                                        {response.isInternal && (
                                                            <span style={{
                                                                fontSize: '12px',
                                                                background: '#f59e0b',
                                                                color: 'white',
                                                                padding: '2px 8px',
                                                                borderRadius: '12px',
                                                                fontWeight: '600'
                                                            }}>
                                                                INTERNAL
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                        {formatDate(response.timestamp)}
                                                    </span>
                                                </div>
                                                <p style={{
                                                    margin: '0',
                                                    lineHeight: '1.6',
                                                    color: '#374151'
                                                }}>
                                                    {response.message}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Quick Actions */}
                            <div style={{
                                borderTop: '1px solid rgba(30, 64, 175, 0.1)',
                                paddingTop: '20px'
                            }}>
                                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
                                    Quick Actions
                                </h4>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                    {selectedTicket.status === 'open' && (
                                        <button
                                            onClick={() => handleStatusUpdate(selectedTicket.ticketId, 'in-progress')}
                                            className="action-button"
                                            style={{ fontSize: '14px', padding: '8px 16px' }}
                                        >
                                            <Clock style={{ width: '16px', height: '16px' }} />
                                            Start Progress
                                        </button>
                                    )}

                                    {(selectedTicket.status === 'open' || selectedTicket.status === 'in-progress') && (
                                        <button
                                            onClick={() => handleResolveWithMessage(selectedTicket)}
                                            className="action-button"
                                            style={{
                                                fontSize: '14px',
                                                padding: '8px 16px',
                                                background: '#059669'
                                            }}
                                        >
                                            <CheckCircle style={{ width: '16px', height: '16px' }} />
                                            Mark Resolved
                                        </button>
                                    )}

                                    <button
                                        className="action-button secondary"
                                        style={{ fontSize: '14px', padding: '8px 16px' }}
                                    >
                                        <MessageCircle style={{ width: '16px', height: '16px' }} />
                                        Add Response
                                    </button>

                                    <button
                                        className="action-button secondary"
                                        style={{ fontSize: '14px', padding: '8px 16px' }}
                                    >
                                        <Mail style={{ width: '16px', height: '16px' }} />
                                        Send Email
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Resolution Modal */}
            {showResolutionModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1001,
                    padding: '20px'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        width: '100%',
                        maxWidth: '500px',
                        padding: '24px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ marginBottom: '20px' }}>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
                                Resolve Ticket
                            </h3>
                            <p style={{ margin: '0', color: '#64748b', fontSize: '14px' }}>
                                Add a resolution message that will be sent to the customer via email.
                            </p>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                                Resolution Message (Optional)
                            </label>
                            <textarea
                                value={resolutionMessage}
                                onChange={(e) => setResolutionMessage(e.target.value)}
                                placeholder="Describe how the issue was resolved or provide additional information..."
                                rows="4"
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    resize: 'vertical',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowResolutionModal(false)}
                                className="action-button secondary"
                                style={{ fontSize: '14px', padding: '8px 16px' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitResolution}
                                className="action-button"
                                style={{
                                    fontSize: '14px',
                                    padding: '8px 16px',
                                    background: '#059669'
                                }}
                            >
                                <CheckCircle style={{ width: '16px', height: '16px' }} />
                                Resolve & Notify Customer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSupportDashboard;
